import React, { useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import {
  Calendar as CalendarIcon,
  CalendarDays,
  CheckCircle,
  RefreshCw,
  Rocket,
  XCircle,
  FileText,
  Loader2,
  CheckCircle2,
  Plus,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square,
  Check,
  CalendarX,
  Sparkles,
  BookOpen,
  Lock,
  X,
  ExternalLink,
  Maximize2,
  Minimize2,
  PenLine,
  Trash2,
  Search,
  AlertCircle,
} from 'lucide-react'
import { Input, Table, Popconfirm, Empty } from 'antd'
import type { ColumnsType } from 'antd/es/table/interface'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useMultiWorkspaceCalendar, contentPlannerKeys } from '@/hooks/useContentPlannerAPI'
import { useDeleteContent } from '@/hooks/useBlogCreationAPI'
import { useTenantStore } from '@/stores/useTenantStore'
import type { CalendarItem, CalendarStats } from '@/types/contentPlanner'
import { CalendarDayCell } from './CalendarDayCell'
import { DayDetailDrawer } from './DayDetailDrawer'
import { ScheduleDraftsDrawer } from './ScheduleDraftsDrawer'
import { StatusItemsDrawer } from './StatusItemsDrawer'
import { StatusBadge } from '../shared/StatusBadge'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { universalBlogEditorPath } from '@/utils/editorRoutes'
import { useAgentFocus, type DirtyEdit } from '@/webmcp/focus'
import type { TenantId } from '@/webmcp/contract'
import { PLANNER_SET_CALENDAR_DATE_EVENT } from '@/webmcp/commands/plannerCalendar'

// ── Types ──────────────────────────────────────────────────────────────────────

interface ContentCalendarViewProps {
  onCreateForDate?: (date: string) => void
  /** Per-item Generate on a day chip — enqueues without opening the wizard. */
  onGenerateItem?: (item: CalendarItem) => void
  onGenerateForDates?: (dates: string[]) => void
}

type CalendarMode = 'month' | 'week'

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDatesInRange(from: string, to: string): string[] {
  const start = dayjs(from)
  const end = dayjs(to)
  const [earlier, later] = start.isBefore(end) ? [start, end] : [end, start]
  const dates: string[] = []
  let current = earlier
  while (!current.isAfter(later)) {
    dates.push(current.format('YYYY-MM-DD'))
    current = current.add(1, 'day')
  }
  return dates
}

// ── Stats bar ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, bg, iconColor, onClick }: {
  label: string; value: number; icon: React.ReactNode; bg: string; iconColor: string
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-xl border bg-card px-4 py-3 text-left w-full transition-all shadow-sm',
        onClick && 'hover:border-foreground/20 hover:shadow-md cursor-pointer active:scale-[0.98]',
        !onClick && 'cursor-default',
      )}
    >
      <span className={cn('flex h-7 w-7 items-center justify-center rounded-md shrink-0', bg, iconColor)}>
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-2xl font-bold leading-none tabular-nums">{value}</p>
        <p className="text-[10px] font-semibold text-muted-foreground leading-none mt-1.5 uppercase tracking-wider">{label}</p>
      </div>
    </button>
  )
}

// ── Inline Day Panel ──────────────────────────────────────────────────────────

const PANEL_STATUS_ORDER: Record<string, number> = {
  pending: 0, generating: 1, draft: 2, completed: 3, published: 4, failed: 5,
}

function DayPanel({
  date,
  items,
  onClose,
  onViewAll,
  onCreateForDate,
  isFocused,
  onToggleFocus,
}: {
  date: string
  items: CalendarItem[]
  onClose: () => void
  onViewAll: (date: string, items: CalendarItem[]) => void
  onCreateForDate?: (date: string) => void
  isFocused?: boolean
  onToggleFocus?: () => void
}) {
  const day = dayjs(date)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { mutate: deleteContent, isPending: isDeleting } = useDeleteContent()
  const [search, setSearch] = useState('')
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set())

  // Reset search + deleted set when switching days
  React.useEffect(() => {
    setSearch('')
    setDeletedIds(new Set())
    setConfirmId(null)
  }, [date])

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return items.filter((item) => {
      if (deletedIds.has(item.id)) return false
      if (!q) return true
      return `${item.title ?? ''} ${item.keyword ?? ''}`.toLowerCase().includes(q)
    })
  }, [items, search, deletedIds])

  const columns: ColumnsType<CalendarItem> = [
    {
      key: 'title',
      title: 'Title',
      ellipsis: true,
      sorter: (a, b) => {
        const la = (a.title || a.keyword || '').toLowerCase()
        const lb = (b.title || b.keyword || '').toLowerCase()
        return la.localeCompare(lb)
      },
      render: (_: unknown, r: CalendarItem) => (
        <div>
          <div className="text-xs font-medium leading-snug line-clamp-2">
            {r.title || r.keyword || 'Untitled'}
          </div>
          {r.word_count != null && (
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {r.word_count.toLocaleString()} words
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      width: 80,
      sorter: (a, b) => (PANEL_STATUS_ORDER[a.status] ?? 9) - (PANEL_STATUS_ORDER[b.status] ?? 9),
      render: (_: unknown, r: CalendarItem) => <StatusBadge status={r.status} size="sm" />,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      render: (_: unknown, r: CalendarItem) => (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            title="Edit article"
            className="p-1 rounded hover:bg-teal-light text-muted-foreground hover:text-teal-dark transition-colors"
            onClick={(e) => { e.stopPropagation(); navigate(universalBlogEditorPath(r.id)) }}
          >
            <PenLine className="h-3 w-3" />
          </button>
          {r.wp_url && (
            <a
              href={r.wp_url}
              target="_blank"
              rel="noopener noreferrer"
              title="View live"
              className="p-1 rounded hover:bg-coral-light text-muted-foreground hover:text-coral-dark transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <Popconfirm
            open={confirmId === r.id}
            title="Delete article?"
            description="Published WP posts will also be removed."
            okText="Delete"
            okButtonProps={{ danger: true, loading: isDeleting }}
            cancelText="Cancel"
            onConfirm={() => {
              deleteContent(r.id, {
                onSuccess: () => {
                  queryClient.invalidateQueries({ queryKey: contentPlannerKeys.all })
                  setDeletedIds((prev) => new Set([...prev, r.id]))
                  setConfirmId(null)
                },
              })
            }}
            onCancel={() => setConfirmId(null)}
            placement="leftTop"
          >
            <button
              title="Delete"
              className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
              onClick={(e) => { e.stopPropagation(); setConfirmId(r.id) }}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ]

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b bg-warm-bg">
        <div>
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">
            {day.format('dddd')}
          </p>
          <p className="text-base font-semibold leading-tight">{day.format('MMMM D, YYYY')}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {items.length === 0 ? 'No articles' : `${filtered.length} of ${items.length} article${items.length > 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-0.5">
          {onToggleFocus && (
            <button
              onClick={onToggleFocus}
              title={isFocused ? 'Exit focus mode' : 'Focus mode — hide calendar'}
              className="rounded-md p-1 hover:bg-muted transition-colors text-muted-foreground"
            >
              {isFocused ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted transition-colors text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Search bar — only when there are articles */}
      {items.length > 0 && (
        <div className="px-3 py-2 border-b bg-warm-bg">
          <Input
            prefix={<Search className="h-3 w-3 text-muted-foreground" />}
            placeholder="Search articles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            size="small"
          />
        </div>
      )}

      {/* Article table / empty state */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {items.length === 0 ? (
          <div className="flex flex-col items-center text-center py-10 gap-3">
            <CalendarIcon className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">Nothing scheduled yet</p>
            {onCreateForDate && (
              <Button
                size="sm"
                className="bg-coral hover:bg-coral-hover text-white gap-1.5 mt-1"
                onClick={() => onCreateForDate(date)}
              >
                <Plus className="h-3.5 w-3.5" />
                Create Content
              </Button>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Empty description="No matches" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          <Table<CalendarItem>
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            size="small"
            pagination={false}
            rowClassName="group cursor-pointer"
            onRow={(record) => ({
              onClick: () => navigate(universalBlogEditorPath(record.id)),
            })}
          />
        )}
      </div>

      {/* Footer */}
      <div className="border-t p-3 flex gap-2">
        {items.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="flex-1 text-xs"
            onClick={() => onViewAll(date, items)}
          >
            Full Detail View
          </Button>
        )}
        {onCreateForDate && (
          <Button
            size="sm"
            className={cn(
              'gap-1.5 bg-coral hover:bg-coral-hover text-white text-xs',
              items.length === 0 ? 'w-full' : 'shrink-0',
            )}
            onClick={() => onCreateForDate(date)}
          >
            <Plus className="h-3.5 w-3.5" />
            {items.length === 0 ? 'Create Content' : ''}
          </Button>
        )}
      </div>
    </div>
  )
}

// ── Day cell ──────────────────────────────────────────────────────────────────

interface DayCellProps {
  day: Dayjs
  isCurrentMonth: boolean
  items: CalendarItem[]
  isSelectMode: boolean
  isSelected: boolean
  isBlocked: boolean
  isPanelActive: boolean
  onInteract: (date: string, items: CalendarItem[], e: React.MouseEvent) => void
  onItemClick?: (item: CalendarItem, e: React.MouseEvent) => void
  onGenerateItem?: (item: CalendarItem) => void
  onCreateForDate?: (date: string) => void
}

function DayCell({
  day,
  isCurrentMonth,
  items,
  isSelectMode,
  isSelected,
  isBlocked,
  isPanelActive,
  onInteract,
  onItemClick,
  onGenerateItem,
  onCreateForDate,
}: DayCellProps) {
  const key = day.format('YYYY-MM-DD')
  const isToday = day.isSame(dayjs(), 'day')

  return (
    <div
      className={cn(
        'relative min-h-[130px] border-b border-r p-2 group transition-all cursor-pointer',
        isSelected
          ? 'bg-teal-light ring-2 ring-inset ring-teal'
          : isPanelActive
          ? 'bg-coral-light/60 ring-1 ring-inset ring-coral/30'
          : isBlocked
          ? 'bg-muted/30'
          : isCurrentMonth
          ? 'bg-card hover:bg-warm-bg'
          : 'bg-muted/20 cursor-default',
      )}
      onClick={(e) => isCurrentMonth && onInteract(key, items, e)}
    >
      {/* Date number */}
      <div className="flex items-center justify-between mb-1">
        {isSelected ? (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal text-white">
            <Check className="w-3.5 h-3.5" />
          </span>
        ) : (
          <span className={cn(
            'inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium',
            isToday
              ? 'bg-teal text-white font-semibold'
              : isPanelActive
              ? 'bg-coral text-white'
              : isCurrentMonth
              ? 'text-foreground'
              : 'text-muted-foreground/40',
          )}>
            {day.date()}
          </span>
        )}

        {/* Right corner icons */}
        {isBlocked && (
          <Lock className="w-3 h-3 text-muted-foreground/60 shrink-0" />
        )}
        {!isSelectMode && !isBlocked && isCurrentMonth && onCreateForDate && (
          <button
            className="hidden group-hover:flex items-center justify-center w-5 h-5 rounded-full
              bg-coral text-white opacity-0 group-hover:opacity-80 hover:!opacity-100
              transition-all duration-150 shrink-0"
            onClick={(e) => {
              e.stopPropagation()
              onCreateForDate(key)
            }}
            title={`Create article for ${key}`}
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
        {isSelectMode && !isSelected && isCurrentMonth && (
          <Square className="hidden group-hover:block w-3.5 h-3.5 text-teal-dark/60 shrink-0" />
        )}
      </div>

      <div className={cn(isBlocked ? 'opacity-40' : '')}>
        <CalendarDayCell
          items={items}
          onItemClick={onItemClick}
          onGenerateItem={onGenerateItem}
          onDayClick={(i) => {
            // handled by the parent div onClick
          }}
        />
      </div>
    </div>
  )
}

// ── Month grid ────────────────────────────────────────────────────────────────

interface GridProps {
  currentDate: Dayjs
  itemsByDate: Map<string, CalendarItem[]>
  isSelectMode: boolean
  selectedDates: Set<string>
  blockedDates: Set<string>
  activePanelDate: string | null
  onInteract: (date: string, items: CalendarItem[], e: React.MouseEvent) => void
  onItemClick?: (item: CalendarItem, e: React.MouseEvent) => void
  onGenerateItem?: (item: CalendarItem) => void
  onCreateForDate?: (date: string) => void
}

function MonthGrid({ currentDate, itemsByDate, isSelectMode, selectedDates, blockedDates, activePanelDate, onInteract, onItemClick, onGenerateItem, onCreateForDate }: GridProps) {
  const startOfMonth = currentDate.startOf('month')
  const daysInMonth = currentDate.daysInMonth()
  const startWeekday = startOfMonth.day()
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7
  const cells = Array.from({ length: totalCells }, (_, i) => startOfMonth.add(i - startWeekday, 'day'))

  return (
    <div>
      <div className="grid grid-cols-7 border-b">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="py-2 text-center text-xs font-medium text-muted-foreground border-r last:border-r-0">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          const key = day.format('YYYY-MM-DD')
          const isLastRow = Math.floor(i / 7) === Math.floor((totalCells - 1) / 7)
          const isLastCol = i % 7 === 6
          return (
            <div key={key} className={cn(isLastRow ? 'border-b-0' : '', isLastCol ? 'border-r-0' : '')}>
              <DayCell
                day={day}
                isCurrentMonth={day.month() === currentDate.month()}
                items={itemsByDate.get(key) ?? []}
                isSelectMode={isSelectMode}
                isSelected={selectedDates.has(key)}
                isBlocked={blockedDates.has(key)}
                isPanelActive={activePanelDate === key}
                onInteract={onInteract}
                onItemClick={onItemClick}
                onGenerateItem={onGenerateItem}
                onCreateForDate={onCreateForDate}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function WeekGrid({ currentDate, itemsByDate, isSelectMode, selectedDates, blockedDates, activePanelDate, onInteract, onItemClick, onGenerateItem, onCreateForDate }: GridProps) {
  const startOfWeek = currentDate.startOf('week')
  const days = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'))

  return (
    <div>
      <div className="grid grid-cols-7 border-b">
        {days.map((day) => {
          const isToday = day.isSame(dayjs(), 'day')
          return (
            <div key={day.format('YYYY-MM-DD')} className="py-3 text-center border-r last:border-r-0">
              <p className="text-xs text-muted-foreground">{day.format('ddd')}</p>
              <span className={cn(
                'inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium mt-0.5 mx-auto',
                isToday ? 'bg-teal text-white' : 'text-foreground',
              )}>
                {day.date()}
              </span>
            </div>
          )
        })}
      </div>
      <div className="grid grid-cols-7 min-h-[400px]">
        {days.map((day) => {
          const key = day.format('YYYY-MM-DD')
          const dayItems = itemsByDate.get(key) ?? []
          const isToday = day.isSame(dayjs(), 'day')
          const isSelected = selectedDates.has(key)
          const isBlocked = blockedDates.has(key)
          const isPanelActive = activePanelDate === key

          return (
            <div
              key={key}
              className={cn(
                'border-r last:border-r-0 p-2 flex flex-col gap-1 transition-all cursor-pointer',
                isSelected
                  ? 'bg-teal-light ring-2 ring-inset ring-teal'
                  : isPanelActive
                  ? 'bg-coral-light/60 ring-1 ring-inset ring-coral/30'
                  : isBlocked
                  ? 'bg-muted/30'
                  : isToday
                  ? 'bg-teal-light/40'
                  : 'bg-card hover:bg-warm-bg',
              )}
              onClick={(e) => onInteract(key, dayItems, e)}
            >
              {isSelected && <div className="flex justify-end"><Check className="w-3.5 h-3.5 text-teal-dark" /></div>}
              {isBlocked && !isSelected && <div className="flex justify-end"><Lock className="w-3 h-3 text-muted-foreground/60" /></div>}
              <div className={cn(isBlocked ? 'opacity-40' : '')}>
                {dayItems.length === 0
                  ? <p className="text-[10px] text-muted-foreground italic mt-1">—</p>
                  : <CalendarDayCell items={dayItems} onItemClick={onItemClick} onGenerateItem={onGenerateItem} onDayClick={() => {}} />
                }
              </div>
              {!isSelectMode && onCreateForDate && (
                <button
                  className="mt-auto flex items-center gap-1 text-[10px] text-muted-foreground hover:text-teal-dark transition-colors opacity-0 hover:opacity-100 pt-1"
                  onClick={(e) => { e.stopPropagation(); onCreateForDate(key) }}
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ContentCalendarView({ onCreateForDate, onGenerateForDates, onGenerateItem }: ContentCalendarViewProps) {
  const routerNavigate = useNavigate()
  const { currentTenant } = useTenantStore()

  // Calendar is scoped to the single selected workspace (no cross-tenant blending).
  const tenantIds = useMemo(
    () => (currentTenant ? [currentTenant] : []),
    [currentTenant],
  )


  // Single-workspace view: never render per-tenant color dots.
  const isMultiWorkspaceMode = false

  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month')
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs())

  const visibleRange = useMemo(() => {
    const start = calendarMode === 'month'
      ? currentDate.startOf('month').startOf('week')
      : currentDate.startOf('week')
    const end = calendarMode === 'month'
      ? currentDate.endOf('month').endOf('week')
      : currentDate.endOf('week')
    return {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
    }
  }, [calendarMode, currentDate])

  const { items: rawItems, isLoading, stats: multiStats } = useMultiWorkspaceCalendar(tenantIds, {
    ...visibleRange,
    limit: 500,
  })

  // Status drawer state
  type StatKey = 'scheduled' | 'draft' | 'generating' | 'published' | 'failed'
  const [statusDrawer, setStatusDrawer] = useState<StatKey | null>(null)

  // Inline day panel
  const [activePanelDate, setActivePanelDate] = useState<string | null>(null)
  const [panelFocused, setPanelFocused] = useState(false)
  const [panelWidth, setPanelWidth] = useState(320)
  const isResizing = useRef(false)
  const resizeStart = useRef({ x: 0, width: 0 })

  // Full detail drawer (opened from panel)
  const [drawerDate, setDrawerDate] = useState<string | null>(null)
  const [drawerItems, setDrawerItems] = useState<CalendarItem[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Multi-select
  const [selectMode, setSelectMode] = useState(false)
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set())
  const [lastClickedDate, setLastClickedDate] = useState<string | null>(null)
  const [scheduleDraftsOpen, setScheduleDraftsOpen] = useState(false)

  const webmcpQueryClient = useQueryClient()

  /**
   * WebMCP dirty verdict for the calendar surface.
   *
   * Published UNCONDITIONALLY on mount, including when nothing is dirty. The
   * veto fails closed on `unknown`, and a surface that never publishes is
   * unread rather than verified-empty, so staying silent refuses every planner
   * write with no visible cause.
   *
   * The one real unsaved buffer this view can lead to is the Schedule Drafts
   * drawer's date-to-article assignments, and that state lives INSIDE
   * `ScheduleDraftsDrawer`, not here. So while it is open we report dirty with
   * `rowId: null`, which the contract defines as widening the veto rather than
   * narrowing it: we know a buffer may exist and cannot name the row it will
   * write to. Lifting that state out of the drawer would narrow this correctly
   * and is deliberately not done in this commit.
   *
   * Selection is NOT reported as dirty. Selected dates are an armed intent, not
   * an unsaved edit, and treating them as dirty would block an agent for as
   * long as an operator happened to have cells highlighted.
   */
  const webmcpDirty = useMemo<readonly DirtyEdit[]>(
    () => (scheduleDraftsOpen ? [{ section: 'calendar', rowId: null }] : []),
    [scheduleDraftsOpen],
  )

  useAgentFocus({
    tenant: (currentTenant as TenantId | null) ?? null,
    surface: 'content-planner-calendar',
    dirty: webmcpDirty,
  })

  /**
   * Page half of the completion handshake.
   *
   * `planner_set_calendar_date` dispatches PLANNER_SET_CALENDAR_DATE_EVENT once
   * its write lands, then blocks on `tool-completion-${requestId}`. Answering it
   * only after the query cache has actually refetched is the entire point: it
   * makes the receipt describe a chip the operator can see on the new day,
   * rather than a write that has merely been accepted.
   */
  useEffect(() => {
    const onToolWrite = (event: Event) => {
      const requestId = (event as CustomEvent<{ requestId?: string }>).detail?.requestId
      if (!requestId) return
      void webmcpQueryClient
        .invalidateQueries({ queryKey: contentPlannerKeys.all })
        .finally(() => {
          window.dispatchEvent(new CustomEvent(`tool-completion-${requestId}`))
        })
    }
    window.addEventListener(PLANNER_SET_CALENDAR_DATE_EVENT, onToolWrite)
    return () => window.removeEventListener(PLANNER_SET_CALENDAR_DATE_EVENT, onToolWrite)
  }, [webmcpQueryClient])

  // Blocked dates — persisted to localStorage
  const blockedKey = `calendarBlocked:${currentTenant}`
  const [blockedDates, setBlockedDates] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(`calendarBlocked:${currentTenant}`)
      return raw ? new Set<string>(JSON.parse(raw)) : new Set()
    } catch { return new Set() }
  })
  useEffect(() => {
    try { localStorage.setItem(blockedKey, JSON.stringify([...blockedDates])) } catch {}
  }, [blockedDates, blockedKey])

  // When NOT in multi-workspace mode, strip _wsColor so chips don't show dots
  const allItems = useMemo<CalendarItem[]>(
    () => isMultiWorkspaceMode ? rawItems : rawItems.map(({ _wsColor: _, ...rest }) => rest),
    [rawItems, isMultiWorkspaceMode],
  )

  // Group items by date
  const itemsByDate = useMemo<Map<string, CalendarItem[]>>(() => {
    const map = new Map<string, CalendarItem[]>()
    for (const item of allItems) {
      if (!item.scheduled_date) continue
      const existing = map.get(item.scheduled_date) ?? []
      map.set(item.scheduled_date, [...existing, item])
    }
    return map
  }, [allItems])

  const stats: CalendarStats = multiStats

  const isEmpty = !isLoading && allItems.length === 0

  // ── Panel resize handler ─────────────────────────────────────────────────────

  function handleResizeMouseDown(e: React.MouseEvent) {
    e.preventDefault()
    isResizing.current = true
    resizeStart.current = { x: e.clientX, width: panelWidth }

    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return
      // Dragging LEFT increases width (panel is on the right)
      const delta = resizeStart.current.x - ev.clientX
      setPanelWidth(Math.min(600, Math.max(256, resizeStart.current.width + delta)))
    }

    const onMouseUp = () => {
      isResizing.current = false
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  // ── Click handler with Ctrl/Shift support ────────────────────────────────────

  function handleDayInteract(date: string, items: CalendarItem[], e: React.MouseEvent) {
    const isCtrl = e.ctrlKey || e.metaKey
    const isShift = e.shiftKey

    if (isCtrl || isShift) {
      e.preventDefault()
      if (isShift && lastClickedDate) {
        // Range select
        const range = getDatesInRange(lastClickedDate, date)
        setSelectedDates((prev) => new Set([...prev, ...range]))
      } else {
        // Ctrl: toggle individual
        setSelectedDates((prev) => {
          const next = new Set(prev)
          if (next.has(date)) next.delete(date)
          else next.add(date)
          return next
        })
      }
      setLastClickedDate(date)
      return
    }

    if (selectMode) {
      // In select mode, any click toggles
      setSelectedDates((prev) => {
        const next = new Set(prev)
        if (next.has(date)) next.delete(date)
        else next.add(date)
        return next
      })
      setLastClickedDate(date)
      return
    }

    // Normal click — open/close inline panel
    setActivePanelDate((prev) => (prev === date ? null : date))
    setLastClickedDate(date)
  }

  function handleItemOpen(item: CalendarItem) {
    routerNavigate(universalBlogEditorPath(item.id))
  }

  function toggleSelectMode() {
    setSelectMode((prev) => {
      if (prev) setSelectedDates(new Set())
      return !prev
    })
  }

  function toggleDateSelect(date: string) {
    setSelectedDates((prev) => {
      const next = new Set(prev)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })
  }

  function handleBlockDates() {
    const count = selectedDates.size
    setBlockedDates((prev) => new Set([...prev, ...selectedDates]))
    setSelectedDates(new Set())
    toast.success(`${count} date${count > 1 ? 's' : ''} reserved`)
  }

  function handleGeneratePosts() {
    const dates = [...selectedDates].sort()
    setSelectedDates(new Set())
    setSelectMode(false)
    onGenerateForDates?.(dates)
  }

  function navigate(direction: 'prev' | 'next') {
    const delta = direction === 'next' ? 1 : -1
    setCurrentDate((d) => d.add(delta, calendarMode === 'month' ? 'month' : 'week'))
  }

  const headerLabel = calendarMode === 'month'
    ? currentDate.format('MMMM YYYY')
    : `${currentDate.startOf('week').format('MMM D')} – ${currentDate.endOf('week').format('MMM D, YYYY')}`

  const sortedSelectedDates = useMemo(() => [...selectedDates].sort(), [selectedDates])

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[68px] rounded-xl" />)}
        </div>
        <Skeleton className="h-[680px] rounded-xl" />
      </div>
    )
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className={cn('grid grid-cols-2 gap-4', stats.failed > 0 ? 'lg:grid-cols-5' : 'lg:grid-cols-4')}>
        <StatCard
          label="Scheduled" value={stats.scheduled}
          icon={<CalendarDays className="h-4 w-4" strokeWidth={1.75} />} bg="bg-warm-bg" iconColor="text-amber-700 dark:text-amber-400"
          onClick={() => setStatusDrawer('scheduled')}
        />
        <StatCard
          label="Ready" value={stats.draft}
          icon={<CheckCircle className="h-4 w-4" strokeWidth={1.75} />} bg="bg-warm-bg" iconColor="text-coral-dark"
          onClick={() => setStatusDrawer('draft')}
        />
        <StatCard
          label="In Progress" value={stats.generating}
          icon={<RefreshCw className={cn('h-4 w-4', stats.generating > 0 && 'animate-spin')} strokeWidth={1.75} />} bg="bg-warm-bg" iconColor="text-teal-dark"
          onClick={() => setStatusDrawer('generating')}
        />
        <StatCard
          label="Published" value={stats.published}
          icon={<Rocket className="h-4 w-4" strokeWidth={1.75} />} bg="bg-warm-bg" iconColor="text-teal-dark"
          onClick={() => setStatusDrawer('published')}
        />
        {stats.failed > 0 && (
          <StatCard
            label="Failed" value={stats.failed}
            icon={<XCircle className="h-4 w-4" strokeWidth={1.75} />} bg="bg-warm-bg" iconColor="text-destructive"
            onClick={() => setStatusDrawer('failed')}
          />
        )}
      </div>

      {/* Calendar + side panel row */}
      <div className="flex gap-4 items-start">
        {/* Calendar card — hidden when panel is in focus mode */}
        <div className={cn('flex-1 min-w-0 rounded-xl border bg-card overflow-hidden shadow-sm', panelFocused && activePanelDate && 'hidden')}>
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b bg-warm-bg">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('prev')}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold text-foreground min-w-[160px] text-center">
                {headerLabel}
              </span>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('next')}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost" size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground ml-1"
                onClick={() => setCurrentDate(dayjs())}
              >
                Today
              </Button>

              <div className="w-px h-5 bg-border mx-1" />

              {/* Select mode toggle */}
              <Button
                variant="ghost" size="sm"
                className={cn(
                  'h-7 px-2.5 text-xs gap-1.5 transition-colors',
                  selectMode ? 'bg-teal text-white hover:bg-teal-hover' : 'text-muted-foreground hover:text-foreground',
                )}
                onClick={toggleSelectMode}
                title="Select multiple dates (or use Ctrl+click / Shift+click)"
              >
                {selectMode ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                {selectMode ? 'Selecting' : 'Select'}
              </Button>
            </div>

            {/* Month/Week toggle */}
            <div className="flex rounded-lg border overflow-hidden text-xs">
              {(['month', 'week'] as CalendarMode[]).map((mode) => (
                <button
                  key={mode}
                  className={cn(
                    'px-3 py-1 font-medium transition-colors capitalize',
                    mode === 'week' && 'border-l',
                    calendarMode === mode ? 'bg-teal text-white' : 'bg-card text-muted-foreground hover:bg-teal-light hover:text-teal-dark',
                  )}
                  onClick={() => setCalendarMode(mode)}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {/* Hints */}
          {selectMode && selectedDates.size === 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-teal-light/60 border-b text-xs text-teal-dark">
              <CheckSquare className="h-3.5 w-3.5 shrink-0" />
              <span>Click days to select. <kbd className="bg-teal-light px-1 rounded">Ctrl</kbd>+click toggles, <kbd className="bg-teal-light px-1 rounded">Shift</kbd>+click selects a range.</span>
            </div>
          )}
          {!selectMode && isEmpty && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-teal-light/60 border-b text-xs text-teal-dark">
              <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
              <span>No content scheduled yet. Click any day or use <strong>+ New Content</strong> above.</span>
            </div>
          )}

          {/* Grid */}
          {calendarMode === 'month' && (
            <MonthGrid
              currentDate={currentDate}
              itemsByDate={itemsByDate}
              isSelectMode={selectMode}
              selectedDates={selectedDates}
              blockedDates={blockedDates}
              activePanelDate={activePanelDate}
              onInteract={handleDayInteract}
              onItemClick={selectMode ? undefined : handleItemOpen}
              onGenerateItem={selectMode ? undefined : onGenerateItem}
              onCreateForDate={onCreateForDate}
            />
          )}
          {calendarMode === 'week' && (
            <WeekGrid
              currentDate={currentDate}
              itemsByDate={itemsByDate}
              isSelectMode={selectMode}
              selectedDates={selectedDates}
              blockedDates={blockedDates}
              activePanelDate={activePanelDate}
              onInteract={handleDayInteract}
              onItemClick={selectMode ? undefined : handleItemOpen}
              onGenerateItem={selectMode ? undefined : onGenerateItem}
              onCreateForDate={onCreateForDate}
            />
          )}

          {/* Action bar */}
          {selectedDates.size > 0 && (
            <div className="border-t bg-card px-4 py-3 flex items-center justify-between flex-wrap gap-3">
              <span className="text-sm font-medium flex items-center gap-1.5">
                <Check className="h-4 w-4 text-teal-dark" />
                {selectedDates.size} date{selectedDates.size > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" className="bg-coral hover:bg-coral-hover text-white gap-1.5 h-8" onClick={handleGeneratePosts}>
                  <Sparkles className="h-3.5 w-3.5" /> Generate Posts
                </Button>
                <Button size="sm" variant="outline" className="border-teal text-teal-dark hover:bg-teal-light gap-1.5 h-8" onClick={() => setScheduleDraftsOpen(true)}>
                  <BookOpen className="h-3.5 w-3.5" /> Schedule Drafts
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={handleBlockDates}>
                  <CalendarX className="h-3.5 w-3.5" /> Reserve
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground" onClick={() => setSelectedDates(new Set())}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Inline day panel */}
        {activePanelDate && (
          <div
            className={cn(
              'relative shrink-0 rounded-xl border bg-card shadow-sm overflow-hidden flex flex-col sticky top-4',
              panelFocused && 'flex-1 shrink',
            )}
            style={panelFocused
              ? { maxHeight: 'calc(100vh - 160px)' }
              : { width: panelWidth, maxHeight: 'calc(100vh - 160px)' }
            }
          >
            {/* Drag-to-resize handle — left edge */}
            {!panelFocused && (
              <div
                className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-teal/30 active:bg-teal/50 transition-colors z-10 group"
                onMouseDown={handleResizeMouseDown}
                title="Drag to resize"
              >
                {/* Visual indicator dot */}
                <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1 h-6 rounded-r bg-border group-hover:bg-teal/50 transition-colors" />
              </div>
            )}
            <DayPanel
              date={activePanelDate}
              items={itemsByDate.get(activePanelDate) ?? []}
              onClose={() => { setActivePanelDate(null); setPanelFocused(false) }}
              onViewAll={(date, items) => {
                setDrawerDate(date)
                setDrawerItems(items)
                setDrawerOpen(true)
              }}
              onCreateForDate={onCreateForDate}
              isFocused={panelFocused}
              onToggleFocus={() => setPanelFocused((f) => !f)}
            />
          </div>
        )}
      </div>

      {/* Full detail drawer (opened from panel "View All Details") */}
      <DayDetailDrawer
        open={drawerOpen}
        date={drawerDate}
        items={drawerItems}
        onClose={() => setDrawerOpen(false)}
        onCreateForDate={(date) => {
          setDrawerOpen(false)
          onCreateForDate?.(date)
        }}
      />

      {/* Schedule drafts drawer */}
      <ScheduleDraftsDrawer
        open={scheduleDraftsOpen}
        dates={sortedSelectedDates}
        allItems={allItems}
        onClose={() => setScheduleDraftsOpen(false)}
        onDone={() => {
          setScheduleDraftsOpen(false)
          setSelectedDates(new Set())
          setSelectMode(false)
        }}
      />

      {/* Status items drawer (opened by clicking a stat card) */}
      {(() => {
        const statConfigs: Record<NonNullable<typeof statusDrawer>, {
          label: string
          icon: React.ReactNode
          filter: (i: CalendarItem) => boolean
        }> = {
          scheduled:  { label: 'Scheduled',  icon: <CalendarIcon className="h-4 w-4" />, filter: (i) => !!i.scheduled_date },
          draft:      { label: 'Drafts',      icon: <FileText className="h-4 w-4" />,    filter: (i) => ['draft', 'completed'].includes(i.status) },
          generating: { label: 'Generating',  icon: <Loader2 className="h-4 w-4" />,     filter: (i) => ['generating', 'pending'].includes(i.status) },
          published:  { label: 'Published',   icon: <CheckCircle2 className="h-4 w-4" />,filter: (i) => i.status === 'published' },
          failed:     { label: 'Failed',      icon: <AlertCircle className="h-4 w-4" />, filter: (i) => i.status === 'failed' },
        }
        const cfg = statusDrawer ? statConfigs[statusDrawer] : null
        return (
          <StatusItemsDrawer
            open={!!statusDrawer}
            statusLabel={cfg?.label ?? ''}
            statusIcon={cfg?.icon ?? null}
            items={cfg ? allItems.filter(cfg.filter) : []}
            onClose={() => setStatusDrawer(null)}
          />
        )
      })()}
    </div>
  )
}
